import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useConfirm } from "../contexts/ConfirmContext";
import Select from "react-select";
import axios from "axios";
import { PlusIcon, TrashIcon, ChevronDownIcon, ChevronUpIcon, PencilSquareIcon, CheckIcon } from "@heroicons/react/24/outline";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import CourseContentManagerModal from "../components/CourseContentManagerModal";

// API URL constant - defined outside component to prevent re-renders
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

// Custom styles for React Select
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    border: "2px solid #000000",
    borderRadius: "0",
    boxShadow: state.isFocused ? "0 0 0 1px #4f46e5" : "none",
    "&:hover": {
      border: "2px solid #4f46e5",
    },
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#e0e7ff",
    borderRadius: "4px",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "#3730a3",
    fontWeight: "500",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "#3730a3",
    "&:hover": {
      backgroundColor: "#c7d2fe",
      color: "#1e1b4b",
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#4f46e5"
      : state.isFocused
      ? "#e0e7ff"
      : "white",
    color: state.isSelected ? "white" : "#374151",
    "&:hover": {
      backgroundColor: state.isSelected ? "#4f46e5" : "#e0e7ff",
    },
  }),
  menu: (provided) => ({
    ...provided,
    border: "2px solid #000000",
    borderRadius: "0",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  }),
};

const NewCourse = () => {
  const navigate = useNavigate();
  const { confirm } = useConfirm();
  const { id: routeId } = useParams();
  const isEditMode = Boolean(routeId);

  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(1);
  const [instructors, setInstructors] = useState([]);
  const [courseId, setCourseId] = useState(isEditMode ? routeId : null);
  const [loadingCourse, setLoadingCourse] = useState(isEditMode);
  const [preview, setPreview] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [selectedLessonIndex, setSelectedLessonIndex] = useState(null);
  const [showAddLessonForm, setShowAddLessonForm] = useState(false);
  const [editingLessonIndex, setEditingLessonIndex] = useState(null);
  const [showContentFormForLesson, setShowContentFormForLesson] = useState(null);
  const [showContentModal, setShowContentModal] = useState(false);
  const [selectedLessonForContent, setSelectedLessonForContent] = useState(null);
  const [expandedClassIndex, setExpandedClassIndex] = useState(0);
  const [contentModalCourseId, setContentModalCourseId] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  // Step 4 review: which sections are expanded (dropdown). Default: all closed except Validation For Publishing Course.
  const [step4Expanded, setStep4Expanded] = useState({ basic: false, pricing: false, classes: false, validation: true });

  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    title: "",
    level: "beginner",
    instructorId: "",
    description: "",
    // Step 2: Pricing & Duration
    duration: "",
    price: "",
    discountType: "percentage", // "percentage" or "total"
    discount: "",
    status: "draft", // Keep status but move it to Step 1 or handle separately
    // Step 3: More Info
    prerequisites: "",
    learningObjectives: "",
    targetAudience: "",
    // Step 4: Categories & Tags
    categories: "",
    tags: "",
    // Step 5: Thumbnail (thumbnail = new File, existingImagePath = stored path when editing)
    thumbnail: null,
    existingImagePath: null,
  });

  const [newLesson, setNewLesson] = useState({
    title: "",
    description: "",
    duration: 0,
    order: 0,
  });

  const [lessonContent, setLessonContent] = useState({
    videoUrl: "",
    note: "",
    ppt: "",
    question: "",
  });

  const [categoryOptions, setCategoryOptions] = useState([
    { value: "programming", label: "Programming" },
    { value: "webdev", label: "Web Development" },
    { value: "datasci", label: "Data Science" },
    { value: "softskills", label: "Soft Skills" },
    { value: "competitive", label: "Competitive Exams" },
    { value: "mobile", label: "Mobile Development" },
    { value: "ai", label: "Artificial Intelligence" },
    { value: "cybersecurity", label: "Cybersecurity" },
    { value: "cloud", label: "Cloud Computing" },
    { value: "devops", label: "DevOps" },
  ]);

  const [tagInput, setTagInput] = useState("");
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryModalInput, setCategoryModalInput] = useState("");
  const [editingCategoryValue, setEditingCategoryValue] = useState(null);
  const [editingCategoryInput, setEditingCategoryInput] = useState("");

  // Fetch instructors
  useEffect(() => {
    const fetchInstructors = async () => {
      try {
        const response = await axios.get(`${API_URL}/users`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        const instructorUsers = response.data.filter(
          (user) => user.role === "instructor" || user.role === "admin"
        );
        setInstructors(instructorUsers);
      } catch (error) {
        console.error("Error fetching instructors:", error);
        toast.error("Failed to load instructors");
      }
    };
    fetchInstructors();
  }, []); // Empty dependency array - only run once on mount

  // Edit mode: fetch course and prefill form + lessons
  useEffect(() => {
    if (!isEditMode || !routeId) return;

    const fetchCourse = async () => {
      try {
        setLoadingCourse(true);
        const response = await axios.get(`${API_URL}/courses/${routeId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        const data = response.data;

        setFormData({
          title: data.title || "",
          level: data.level || "beginner",
          instructorId: data.instructor_id ? String(data.instructor_id) : "",
          description: data.description || "",
          duration: data.duration != null ? String(data.duration) : "",
          price: data.price != null ? String(data.price) : "",
          discountType: "percentage",
          discount: "",
          status: data.status || "draft",
          prerequisites: data.prerequisites || "",
          learningObjectives: data.learning_objectives || "",
          targetAudience: data.target_audience || "",
          categories: Array.isArray(data.categories) ? data.categories.join(", ") : (data.categories || ""),
          tags: Array.isArray(data.tags) ? data.tags.join(", ") : (data.tags || ""),
          thumbnail: null,
          existingImagePath: data.image_path || null,
        });
        const thumb = data.thumbnail || data.image;
        const baseUrl = API_URL.replace(/\/api\/?$/, "");
        setPreview(thumb ? (thumb.startsWith("http") ? thumb : baseUrl + (thumb.startsWith("/") ? thumb : "/" + thumb)) : null);

        if (data.lessons && data.lessons.length > 0) {
          const mappedLessons = data.lessons.map((l, i) => ({
            id: l.id,
            title: l.title || "",
            description: l.description || "",
            duration: l.duration ?? 0,
            order: l.order ?? i + 1,
            contents: l.contents || [],
            videoUrl: l.video_url ?? "",
            url: l.url ?? "",
            show_class: l.show_class != null ? Boolean(l.show_class) : true,
            show_note: l.show_note != null ? Boolean(l.show_note) : true,
            show_ppt: l.show_ppt != null ? Boolean(l.show_ppt) : true,
            show_question: l.show_question != null ? Boolean(l.show_question) : true,
          }));
          setLessons(mappedLessons);
        }

        setCourseId(data.id);
      } catch (error) {
        console.error("Error fetching course:", error);
        toast.error("Failed to load course");
        navigate("/courses");
      } finally {
        setLoadingCourse(false);
      }
    };

    fetchCourse();
  }, [isEditMode, routeId, navigate]);

  // Ensure course is created before adding lessons - Disabled to prevent infinite loops
  // Users can manually save using "Save as Draft" button
  // useEffect(() => {
  //   if (currentStep >= 3 && !courseId && formData.title) {
  //     handleSaveDraft(true);
  //   }
  // }, [currentStep]);

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "thumbnail" && files && files[0]) {
      setFormData((prev) => ({ ...prev, thumbnail: files[0] }));
      setPreview(URL.createObjectURL(files[0]));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      // Clear error when user starts typing - use functional update to avoid stale closure
      setErrors((prev) => {
        if (prev[name]) {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        }
        return prev; // Return same reference if no change
      });
    }
  };

  const handleCategoryChange = (selected) => {
    const categoriesString = selected
      ? selected.map((cat) => cat.label).join(", ")
      : "";
    setFormData((prev) => ({ ...prev, categories: categoriesString }));
  };

  const handleCreateCategory = (inputValue) => {
    const newOption = { value: inputValue.toLowerCase(), label: inputValue };
    setCategoryOptions((prev) => [...prev, newOption]);
    const currentCategories = parseCategories(formData.categories);
    const updatedCategories = [...currentCategories, newOption];
    const categoriesString = updatedCategories.map((cat) => cat.label).join(", ");
    setFormData((prev) => ({ ...prev, categories: categoriesString }));
  };

  const parseCategories = (categoriesString) => {
    if (!categoriesString) return [];
    return categoriesString
      .split(",")
      .map((cat) => cat.trim())
      .filter(Boolean)
      .map((cat) => ({
        value: cat.toLowerCase(),
        label: cat,
      }));
  };

  const handleAddCategoryFromModal = () => {
    const name = categoryModalInput.trim();
    if (!name) return;
    const value = name.toLowerCase();
    if (categoryOptions.some((c) => c.value === value)) {
      toast.info("Category already exists");
      return;
    }
    setCategoryOptions((prev) => [...prev, { value, label: name }]);
    setCategoryModalInput("");
    toast.success(`Category "${name}" added`);
  };

  const handleStartEditCategory = (cat) => {
    setEditingCategoryValue(cat.value);
    setEditingCategoryInput(cat.label);
  };

  const handleSaveEditCategory = () => {
    if (editingCategoryValue == null) return;
    const newLabel = editingCategoryInput.trim();
    if (!newLabel) {
      setEditingCategoryValue(null);
      setEditingCategoryInput("");
      return;
    }
    const newValue = newLabel.toLowerCase();
    const prev = categoryOptions.find((c) => c.value === editingCategoryValue);
    if (!prev) return;
    setCategoryOptions((prevOpts) =>
      prevOpts.map((c) =>
        c.value === editingCategoryValue ? { value: newValue, label: newLabel } : c
      )
    );
    setFormData((prev) => {
      const current = (prev.categories || "").split(",").map((s) => s.trim()).filter(Boolean);
      const updated = current.map((s) => (s === prev.label ? newLabel : s));
      return { ...prev, categories: updated.join(", ") };
    });
    setEditingCategoryValue(null);
    setEditingCategoryInput("");
    toast.success("Category updated");
  };

  const handleDeleteCategoryFromModal = (cat) => {
    setCategoryOptions((prev) => prev.filter((c) => c.value !== cat.value));
    setFormData((prev) => {
      const current = (prev.categories || "").split(",").map((s) => s.trim()).filter(Boolean);
      const updated = current.filter((s) => s !== cat.label);
      return { ...prev, categories: updated.join(", ") };
    });
    if (editingCategoryValue === cat.value) {
      setEditingCategoryValue(null);
      setEditingCategoryInput("");
    }
    toast.success(`Category "${cat.label}" removed`);
  };

  const handleTagInput = (e) => {
    setTagInput(e.target.value);
  };

  const handleTagKeyDown = (e) => {
    if ((e.key === "," || e.key === "Enter") && tagInput.trim()) {
      e.preventDefault();
      const tagsArr = formData.tags
        ? formData.tags.split(",").map((t) => t.trim())
        : [];
      if (!tagsArr.includes(tagInput.trim())) {
        setFormData((prev) => ({
          ...prev,
          tags: tagsArr.concat(tagInput.trim()).join(","),
        }));
      }
      setTagInput("");
    }
  };

  const handleRemoveTag = (tag) => {
    const tagsArr = formData.tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    setFormData((prev) => ({
      ...prev,
      tags: tagsArr.filter((t) => t !== tag).join(","),
    }));
  };

  const handleAddLesson = () => {
    if (!newLesson.title.trim()) {
      toast.error("Please enter a class name");
      return;
    }
    const maxOrder = lessons.reduce((m, l) => Math.max(m, Number(l.order) || 0), 0);
    const lesson = {
      ...newLesson,
      order: maxOrder + 1,
      contents: [],
      videoUrl: newLesson.videoUrl ?? "",
      url: newLesson.url ?? "",
      show_class: newLesson.show_class !== undefined ? newLesson.show_class : true,
      show_note: newLesson.show_note !== undefined ? newLesson.show_note : true,
      show_ppt: newLesson.show_ppt !== undefined ? newLesson.show_ppt : true,
      show_question: newLesson.show_question !== undefined ? newLesson.show_question : true,
    };
    // Append so new class is last in admin and on iSyllabi (sidebar uses order ASC)
    setLessons([...lessons, lesson]);
    setExpandedClassIndex(lessons.length); // New class is last; keep its section open
    setNewLesson({ title: "", description: "", duration: 0, order: 0 });
    setShowAddLessonForm(false);
    toast.success("Class added successfully!");
  };

  const handleEditLesson = (index) => {
    setEditingLessonIndex(index);
    setNewLesson({ ...lessons[index] });
    setShowAddLessonForm(true);
  };

  const handleUpdateLesson = () => {
    if (!newLesson.title.trim()) {
      toast.error("Please enter a class name");
      return;
    }
    const updatedLessons = [...lessons];
    updatedLessons[editingLessonIndex] = newLesson;
    setLessons(updatedLessons);
    setNewLesson({ title: "", description: "", duration: 0, order: 0 });
    setShowAddLessonForm(false);
    setEditingLessonIndex(null);
    toast.success("Class updated successfully!");
  };

  const handleDeleteLesson = async (index) => {
    const ok = await confirm({
      title: "Delete class",
      message: "Are you sure you want to delete this class?",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      variant: "danger",
    });
    if (!ok) return;
    const toRemove = lessons[index];
    if (toRemove?.id) {
      try {
        await axios.delete(`${API_URL}/lessons/${toRemove.id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
      } catch (err) {
        console.error("Delete lesson failed:", err);
        toast.error(
          err.response?.data?.error ||
            err.response?.data?.message ||
            "Failed to delete class on server"
        );
        return;
      }
    }
    const updatedLessons = lessons.filter((_, i) => i !== index);
    setLessons(updatedLessons);
    if (selectedLessonIndex === index) {
      setSelectedLessonIndex(null);
    } else if (selectedLessonIndex !== null && selectedLessonIndex > index) {
      setSelectedLessonIndex(selectedLessonIndex - 1);
    }
    if (showContentFormForLesson === index) {
      setShowContentFormForLesson(null);
    } else if (
      showContentFormForLesson !== null &&
      showContentFormForLesson > index
    ) {
      setShowContentFormForLesson(showContentFormForLesson - 1);
    }
    if (expandedClassIndex === index) {
      setExpandedClassIndex(null);
    } else if (expandedClassIndex !== null && expandedClassIndex > index) {
      setExpandedClassIndex(expandedClassIndex - 1);
    }
    toast.success("Class deleted successfully!");
  };

  const handleSaveLessonContent = async (lessonIndex) => {
    if (!courseId) {
      toast.error("Please save the course first");
      return;
    }

    const lesson = lessons[lessonIndex];
    if (!lesson.id) {
      // Create lesson first
      try {
        const response = await axios.post(
          `${API_URL}/lessons`,
          {
            course_id: courseId,
            title: lesson.title,
            description: lesson.description,
            duration: lesson.duration,
            order: lesson.order,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const updatedLessons = [...lessons];
        updatedLessons[lessonIndex] = { ...lesson, id: response.data.id };
        setLessons(updatedLessons);
        lesson.id = response.data.id;
      } catch (error) {
        toast.error("Failed to create lesson");
        return;
      }
    }

    // Save content
    const content = lessonContent;
    const contentsToSave = [];

    if (content.videoUrl) {
      contentsToSave.push({
        lesson_id: lesson.id,
        type: "class",
        url: content.videoUrl,
        content: "",
      });
    }

    if (content.note) {
      contentsToSave.push({
        lesson_id: lesson.id,
        type: "note",
        content: content.note,
        url: "",
      });
    }

    if (content.ppt) {
      contentsToSave.push({
        lesson_id: lesson.id,
        type: "ppt",
        content: content.ppt,
        url: "",
      });
    }

    if (content.question) {
      contentsToSave.push({
        lesson_id: lesson.id,
        type: "question",
        content: content.question,
        url: "",
      });
    }

    try {
      for (const contentItem of contentsToSave) {
        await axios.post(`${API_URL}/contents`, contentItem, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
      }
      toast.success("Lesson content saved successfully!");
      setLessonContent({ videoUrl: "", note: "", ppt: "", question: "" });
    } catch (error) {
      toast.error("Failed to save lesson content");
    }
  };

  // Validation function that doesn't update state (for checking only)
  const checkStepValid = (step) => {
    const validationErrors = {};

    switch (step) {
      case 1:
        if (!formData.title || formData.title.trim() === "") {
          validationErrors.title = "Title is required";
        }
        if (!formData.instructorId || formData.instructorId === "") {
          validationErrors.instructorId = "Instructor is required";
        }
        break;
      case 2:
        if (formData.duration && (isNaN(formData.duration) || parseFloat(formData.duration) <= 0)) {
          validationErrors.duration = "Duration must be a positive number";
        }
        if (formData.price && (isNaN(formData.price) || parseFloat(formData.price) < 0)) {
          validationErrors.price = "Price must be a positive number";
        }
        if (formData.discount) {
          if (formData.discountType === "percentage") {
            if (isNaN(formData.discount) || parseFloat(formData.discount) < 0 || parseFloat(formData.discount) > 100) {
              validationErrors.discount = "Discount percentage must be between 0 and 100";
            }
          } else if (formData.discountType === "total") {
            if (isNaN(formData.discount) || parseFloat(formData.discount) < 0) {
              validationErrors.discount = "Discount amount must be a positive number";
            }
            if (formData.price && parseFloat(formData.discount) > parseFloat(formData.price)) {
              validationErrors.discount = "Discount cannot be greater than the price";
            }
          }
        }
        break;
      case 3:
        if (lessons.length === 0) {
          validationErrors._general = "Please add at least one class";
        }
        break;
      default:
        break;
    }

    return Object.keys(validationErrors).length === 0;
  };

  // Validation function that updates state (for form submission)
  const validateStep = (step) => {
    setErrors({});
    const validationErrors = {};

    switch (step) {
      case 1:
        if (!formData.title || formData.title.trim() === "") {
          validationErrors.title = "Title is required";
        }
        if (!formData.instructorId || formData.instructorId === "") {
          validationErrors.instructorId = "Instructor is required";
        }
        break;
      case 2:
        if (formData.duration && (isNaN(formData.duration) || parseFloat(formData.duration) <= 0)) {
          validationErrors.duration = "Duration must be a positive number";
        }
        if (formData.price && (isNaN(formData.price) || parseFloat(formData.price) < 0)) {
          validationErrors.price = "Price must be a positive number";
        }
        if (formData.discount) {
          if (formData.discountType === "percentage") {
            if (isNaN(formData.discount) || parseFloat(formData.discount) < 0 || parseFloat(formData.discount) > 100) {
              validationErrors.discount = "Discount percentage must be between 0 and 100";
            }
          } else if (formData.discountType === "total") {
            if (isNaN(formData.discount) || parseFloat(formData.discount) < 0) {
              validationErrors.discount = "Discount amount must be a positive number";
            }
            if (formData.price && parseFloat(formData.discount) > parseFloat(formData.price)) {
              validationErrors.discount = "Discount cannot be greater than the price";
            }
          }
        }
        break;
      case 3:
        if (lessons.length === 0) {
          validationErrors._general = "Please add at least one class";
        }
        break;
      default:
        break;
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return false;
    }

    return true;
  };

  /** Validates all steps required for publishing (1: basic info, 2: pricing, 3: at least one class). Returns false and sets errors if invalid. */
  const validateForPublish = () => {
    setErrors({});
    const validationErrors = {};
    // Step 1
    if (!formData.title || formData.title.trim() === "") {
      validationErrors.title = "Title is required";
    }
    if (!formData.instructorId || formData.instructorId === "") {
      validationErrors.instructorId = "Instructor is required";
    }
    // Step 2
    if (formData.duration && (isNaN(formData.duration) || parseFloat(formData.duration) <= 0)) {
      validationErrors.duration = "Duration must be a positive number";
    }
    if (formData.price && (isNaN(formData.price) || parseFloat(formData.price) < 0)) {
      validationErrors.price = "Price must be a positive number";
    }
    if (formData.discount) {
      if (formData.discountType === "percentage") {
        if (isNaN(formData.discount) || parseFloat(formData.discount) < 0 || parseFloat(formData.discount) > 100) {
          validationErrors.discount = "Discount percentage must be between 0 and 100";
        }
      } else if (formData.discountType === "total") {
        if (isNaN(formData.discount) || parseFloat(formData.discount) < 0) {
          validationErrors.discount = "Discount amount must be a positive number";
        }
        if (formData.price && parseFloat(formData.discount) > parseFloat(formData.price)) {
          validationErrors.discount = "Discount cannot be greater than the price";
        }
      }
    }
    // Step 3
    if (lessons.length === 0) {
      validationErrors._general = "Please add at least one class before publishing.";
    }
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < 4) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSaveDraft = async (silent = false, publish = false) => {
    if (savingDraft) return;
    if (!silent) {
      setSavingDraft(true);
      console.log("Save as Draft clicked", { courseId, isEditMode });
    }
    try {
      // Use null for optional fields so backend never receives undefined (SQL bind error)
      const courseData = {
        title: formData.title ?? "",
        description: formData.description ?? "",
        level: formData.level ?? "beginner",
        duration: formData.duration ?? "",
        price: formData.price != null && formData.price !== "" ? Number(formData.price) : null,
        discount_type: formData.discountType || null,
        discount: formData.discount != null && formData.discount !== "" ? formData.discount : null,
        status: publish ? "published" : "draft", // Save as Draft = draft; Update & Publish = published
        teacher_id: formData.instructorId || null,
        instructor_id: formData.instructorId || null,
        prerequisites: formData.prerequisites || null,
        learning_objectives: formData.learningObjectives || null,
        target_audience: formData.targetAudience || null,
        categories: formData.categories || null,
        tags: formData.tags || null,
      };
      // Preserve existing thumbnail when editing and not uploading a new image
      if (courseId && !formData.thumbnail && formData.existingImagePath != null) {
        courseData.image = formData.existingImagePath;
      }

      let response;
      let savedCourseId = courseId;
      if (courseId) {
        response = await axios.put(`${API_URL}/courses/${courseId}`, courseData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
      } else {
        response = await axios.post(`${API_URL}/courses`, courseData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        savedCourseId = response.data.id;
        setCourseId(savedCourseId);
      }

      // Redirect immediately after save when user clicked "Save as Draft" (so redirect isn't blocked by thumbnail upload). Don't redirect when saving for publish (handleSubmit will redirect).
      if (!silent && !publish) {
        toast.success("Course saved as draft successfully!");
        setErrors({});
        // Defer redirect so it runs after React commits state and isn't batched away
        setTimeout(() => navigate("/courses", { replace: true }), 0);
      }

      if (formData.thumbnail) {
        const formDataUpload = new FormData();
        formDataUpload.append("image", formData.thumbnail);
        formDataUpload.append("type", "course");

        await axios.post(`${API_URL}/upload/image`, formDataUpload, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
          params: {
            courseId: savedCourseId,
          },
        });
      }

      return savedCourseId;
    } catch (error) {
      console.error("Error saving draft:", error);
      // Handle API errors and map to fields
      const apiErrors = {};
      if (error.response?.data?.errors) {
        // Laravel-style validation errors
        Object.keys(error.response.data.errors).forEach((field) => {
          apiErrors[field] = Array.isArray(error.response.data.errors[field])
            ? error.response.data.errors[field][0]
            : error.response.data.errors[field];
        });
      } else if (error.response?.data?.message) {
        // Single error message - try to map to fields
        const message = error.response.data.message.toLowerCase();
        if (message.includes('title')) {
          apiErrors.title = error.response.data.message;
        } else if (message.includes('description')) {
          apiErrors.description = error.response.data.message;
        } else if (message.includes('instructor') || message.includes('teacher')) {
          apiErrors.instructorId = error.response.data.message;
        } else if (message.includes('duration')) {
          apiErrors.duration = error.response.data.message;
        } else if (message.includes('price')) {
          apiErrors.price = error.response.data.message;
        } else if (message.includes('discount')) {
          apiErrors.discount = error.response.data.message;
        } else {
          apiErrors._general = error.response.data.message;
        }
      } else if (error.response?.data?.error) {
        apiErrors._general = error.response.data.error;
      } else {
        apiErrors._general = error.message || "An unexpected error occurred";
      }
      
      setErrors(apiErrors);
      
      if (!silent) {
        // Only show toast for general errors, field-specific errors are shown below fields
        if (!error.response?.data?.errors && apiErrors._general) {
          toast.error(apiErrors._general);
        }
      }
      
      return null; // Return null on error
    } finally {
      if (!silent) setSavingDraft(false);
    }
  };

  const handlePreview = () => {
    toast.info("Preview functionality coming soon!");
  };

  const handleSubmit = async () => {
    if (!validateForPublish()) {
      toast.error("Please fix the errors below before publishing. Title, instructor, and at least one class are required.");
      return;
    }

    try {
      // Save course with status published (activates course)
      await handleSaveDraft(true, true);

      // Save all lessons (create new, update existing in edit mode) with full fields so video/link/visibility are not lost
      if (lessons.length > 0 && courseId) {
        const authHeaders = {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        };
        for (let i = 0; i < lessons.length; i++) {
          const lesson = lessons[i];
          const payload = {
            title: lesson.title,
            description: lesson.description,
            duration: lesson.duration,
            order: lesson.order,
            videoUrl: lesson.videoUrl ?? undefined,
            url: lesson.url ?? undefined,
            show_class: lesson.show_class !== undefined ? (lesson.show_class ? 1 : 0) : undefined,
            show_note: lesson.show_note !== undefined ? (lesson.show_note ? 1 : 0) : undefined,
            show_ppt: lesson.show_ppt !== undefined ? (lesson.show_ppt ? 1 : 0) : undefined,
            show_question: lesson.show_question !== undefined ? (lesson.show_question ? 1 : 0) : undefined,
          };
          if (lesson.id) {
            await axios.put(
              `${API_URL}/lessons/${lesson.id}`,
              payload,
              authHeaders
            );
          } else {
            const response = await axios.post(
              `${API_URL}/lessons`,
              { course_id: courseId, ...payload },
              authHeaders
            );
            lessons[i].id = response.data.id;
          }
        }
      }

      toast.success(isEditMode ? "Course updated and published successfully!" : "Course created and published successfully!");
      setErrors({});
      navigate("/courses");
    } catch (error) {
      console.error("Error creating course:", error);
      // Handle API errors and map to fields
      const apiErrors = {};
      if (error.response?.data?.errors) {
        Object.keys(error.response.data.errors).forEach((field) => {
          apiErrors[field] = Array.isArray(error.response.data.errors[field])
            ? error.response.data.errors[field][0]
            : error.response.data.errors[field];
        });
      } else if (error.response?.data?.message) {
        const message = error.response.data.message.toLowerCase();
        if (message.includes('title')) {
          apiErrors.title = error.response.data.message;
        } else if (message.includes('description')) {
          apiErrors.description = error.response.data.message;
        } else if (message.includes('instructor') || message.includes('teacher')) {
          apiErrors.instructorId = error.response.data.message;
        } else if (message.includes('duration')) {
          apiErrors.duration = error.response.data.message;
        } else if (message.includes('price')) {
          apiErrors.price = error.response.data.message;
        } else if (message.includes('discount')) {
          apiErrors.discount = error.response.data.message;
        } else {
          apiErrors._general = error.response.data.message;
        }
      } else if (error.response?.data?.error) {
        apiErrors._general = error.response.data.error;
      } else {
        apiErrors._general = error.message || "An unexpected error occurred";
      }
      
      setErrors(apiErrors);
      
      // Only show toast for general errors, field-specific errors are shown below fields
      if (!error.response?.data?.errors && apiErrors._general) {
        toast.error(apiErrors._general);
      }
    }
  };

  const tagsArr = formData.tags
    ? formData.tags.split(",").map((t) => t.trim()).filter(Boolean)
    : [];

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            {errors._general && (
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-600">{errors._general}</p>
              </div>
            )}
            <h3 className="text-xl font-bold text-gray-800 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Course Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    required
                    placeholder="e.g. Introduction to Python"
                    value={formData.title}
                    onChange={handleInputChange}
                    className={`w-full rounded-lg border-2 shadow-sm focus:ring-2 focus:ring-offset-2 sm:text-sm placeholder-gray-400 pl-4 py-3 tracking-wide text-base bg-white transition-all ${
                      errors.title
                        ? "border-red-500 focus:border-red-600 focus:ring-red-500"
                        : "border-indigo-300 focus:border-indigo-600 focus:ring-indigo-500"
                    }`}
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                  )}
                </div>

                {/* Level */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Level
                  </label>
                  <select
                    name="level"
                    value={formData.level}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border-2 border-indigo-300 shadow-sm focus:border-indigo-600 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm pl-4 py-3 tracking-wide text-base bg-white transition-all"
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>

                {/* Learning Objectives */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Learning Objectives
                  </label>
                  <textarea
                    name="learningObjectives"
                    placeholder="e.g. Understand Python basics, write simple scripts..."
                    value={formData.learningObjectives}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full rounded-lg border-2 border-indigo-300 shadow-sm focus:border-indigo-600 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm placeholder-gray-400 pl-4 py-3 tracking-wide text-base bg-white transition-all"
                  />
                </div>

                {/* Instructor - in left column, same row as Target Audience */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Instructor <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="instructorId"
                    value={formData.instructorId}
                    onChange={handleInputChange}
                    required
                    className={`w-full rounded-lg border-2 shadow-sm focus:ring-2 focus:ring-offset-2 sm:text-sm pl-4 py-3 tracking-wide text-base bg-white transition-all ${
                      errors.instructorId
                        ? "border-red-500 focus:border-red-600 focus:ring-red-500"
                        : "border-indigo-300 focus:border-indigo-600 focus:ring-indigo-500"
                    }`}
                  >
                    <option value="">Select Instructor</option>
                    {instructors.map((inst) => (
                      <option key={inst.id} value={inst.id}>
                        {inst.firstName} {inst.lastName} ({inst.email})
                      </option>
                    ))}
                  </select>
                  {errors.instructorId && (
                    <p className="mt-1 text-sm text-red-600">{errors.instructorId}</p>
                  )}
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                {/* Categories */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Categories
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowCategoryModal(true)}
                      className="p-1.5 rounded-md text-indigo-600 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      title="Manage categories"
                    >
                      <PlusIcon className="w-5 h-5" />
                    </button>
                  </div>
                  <Select
                    options={categoryOptions}
                    isMulti
                    isClearable
                    isSearchable
                    placeholder="Select categories..."
                    onChange={handleCategoryChange}
                    value={parseCategories(formData.categories)}
                    styles={selectStyles}
                  />
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {tagsArr.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium"
                      >
                        {tag}
                        <button
                          type="button"
                          className="ml-2 text-indigo-500 hover:text-red-500"
                          onClick={() => handleRemoveTag(tag)}
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                  </div>
                  <input
                    type="text"
                    placeholder="Type a tag and press Enter or ,"
                    value={tagInput}
                    onChange={handleTagInput}
                    onKeyDown={handleTagKeyDown}
                    className="w-full rounded-lg border-2 border-indigo-300 shadow-sm focus:border-indigo-600 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm placeholder-gray-400 pl-4 py-3 tracking-wide text-base bg-white transition-all"
                  />
                </div>

                {/* Prerequisites */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prerequisites
                  </label>
                  <textarea
                    name="prerequisites"
                    placeholder="e.g. Basic computer knowledge"
                    value={formData.prerequisites}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full rounded-lg border-2 border-indigo-300 shadow-sm focus:border-indigo-600 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm placeholder-gray-400 pl-4 py-3 tracking-wide text-base bg-white transition-all"
                  />
                </div>

                {/* Target Audience - in right column, same row as Instructor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Audience
                  </label>
                  <textarea
                    name="targetAudience"
                    placeholder="e.g. Beginners, students, professionals..."
                    value={formData.targetAudience}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full rounded-lg border-2 border-indigo-300 shadow-sm focus:border-indigo-600 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm placeholder-gray-400 pl-4 py-3 tracking-wide text-base bg-white transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Description - Full width single row */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                placeholder="Briefly describe the course..."
                value={formData.description}
                onChange={handleInputChange}
                rows="4"
                className={`w-full rounded-lg border-2 shadow-sm focus:ring-2 focus:ring-offset-2 sm:text-sm placeholder-gray-400 pl-4 py-3 tracking-wide text-base bg-white transition-all ${
                  errors.description
                    ? "border-red-500 focus:border-red-600 focus:ring-red-500"
                    : "border-indigo-300 focus:border-indigo-600 focus:ring-indigo-500"
                }`}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">{errors.description}</p>
              )}
            </div>

            {/* Thumbnail - Full width single row */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Thumbnail
              </label>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <input
                    type="file"
                    name="thumbnail"
                    onChange={handleInputChange}
                    accept="image/*"
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 pl-4 py-2 tracking-wide text-base"
                  />
                </div>
                {preview && (
                  <div>
                    <img
                      src={preview}
                      alt="Thumbnail Preview"
                      className="h-48 w-full rounded-lg shadow-md border-2 border-indigo-200 object-cover"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-6">
            {errors._general && (
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-600">{errors._general}</p>
              </div>
            )}
            <h3 className="text-xl font-bold text-gray-800 mb-4">Pricing & Duration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duration (minutes)
                </label>
                <input
                  type="number"
                  name="duration"
                  min="0"
                  placeholder="e.g. 90"
                  value={formData.duration}
                  onChange={handleInputChange}
                  className={`w-full rounded-none border-2 shadow-sm focus:ring-1 sm:text-sm placeholder-gray-400 pl-4 py-2 tracking-wide text-lg ${
                    errors.duration
                      ? "border-red-500 focus:border-red-600 focus:ring-red-600"
                      : "border-black focus:border-indigo-600 focus:ring-indigo-600"
                  }`}
                />
                {errors.duration && (
                  <p className="mt-1 text-sm text-red-600">{errors.duration}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price <span className="text-indigo-600">(₹)</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600 font-semibold text-lg">
                    ₹
                  </span>
                  <input
                    type="number"
                    name="price"
                    min="0"
                    step="1"
                    placeholder="e.g. 2999"
                    value={formData.price}
                    onChange={handleInputChange}
                    className={`w-full rounded-lg border-2 shadow-sm focus:ring-2 focus:ring-offset-2 sm:text-sm pl-10 pr-4 py-3 tracking-wide text-base bg-white transition-all placeholder-gray-400 ${
                      errors.price
                        ? "border-red-500 focus:border-red-600 focus:ring-red-500"
                        : "border-indigo-300 focus:border-indigo-600 focus:ring-indigo-500"
                    }`}
                  />
                </div>
                {errors.price && (
                  <p className="mt-1 text-sm text-red-600">{errors.price}</p>
                )}
              </div>
            </div>
            <div className="border-t-2 border-indigo-200 pt-6 mt-6 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
                <h4 className="text-xl font-bold text-gray-800">Discount & Offers</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Discount Type <span className="text-indigo-600">*</span>
                  </label>
                  <select
                    name="discountType"
                    value={formData.discountType}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border-2 border-indigo-300 shadow-sm focus:border-indigo-600 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm pl-4 py-3 tracking-wide text-base bg-white transition-all"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="total">Fixed Amount (₹)</option>
                  </select>
                  <p className="mt-2 text-xs text-gray-500">
                    {formData.discountType === "percentage"
                      ? "Discount will be calculated as a percentage of the original price"
                      : "Discount will be a fixed amount deducted from the original price"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Discount Value{" "}
                    <span className="text-indigo-600">
                      {formData.discountType === "percentage" ? "(%)" : "(₹)"}
                    </span>
                  </label>
                  <div className="relative">
                    {formData.discountType === "total" && (
                      <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600 font-semibold text-lg">
                        ₹
                      </span>
                    )}
                    <input
                      type="number"
                      name="discount"
                      min="0"
                      step={formData.discountType === "percentage" ? "0.01" : "1"}
                      max={formData.discountType === "percentage" ? "100" : undefined}
                      placeholder={
                        formData.discountType === "percentage"
                          ? "e.g. 15 (for 15% off)"
                          : "e.g. 500 (for ₹500 off)"
                      }
                      value={formData.discount}
                      onChange={handleInputChange}
                      className={`w-full rounded-lg border-2 shadow-sm focus:ring-2 focus:ring-offset-2 sm:text-sm py-3 tracking-wide text-base bg-white transition-all ${
                        formData.discountType === "total" ? "pl-10 pr-4" : "pl-4 pr-4"
                      } ${
                        errors.discount
                          ? "border-red-500 focus:border-red-600 focus:ring-red-500"
                          : "border-indigo-300 focus:border-indigo-600 focus:ring-indigo-500"
                      }`}
                    />
                    {formData.discountType === "percentage" && (
                      <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-600 font-semibold">
                        %
                      </span>
                    )}
                  </div>
                  {errors.discount && (
                    <p className="mt-1 text-sm text-red-600">{errors.discount}</p>
                  )}
                </div>
              </div>
              
              {/* Discount Preview Card */}
              {formData.discount && formData.price && parseFloat(formData.discount) > 0 && (
                <div className="mt-6 bg-white rounded-xl border-2 border-indigo-200 p-5 shadow-md">
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                      Price Breakdown
                    </h5>
                    <div className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">
                      SAVINGS
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                      <span className="text-gray-600 font-medium">Original Price:</span>
                      <span className="text-lg font-bold text-gray-800">
                        ₹{parseFloat(formData.price).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    {formData.discountType === "percentage" ? (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">
                            Discount ({formData.discount}%):
                          </span>
                          <span className="text-lg font-bold text-red-600">
                            - ₹
                            {(
                              (parseFloat(formData.price) * parseFloat(formData.discount)) /
                              100
                            ).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                        <div className="flex justify-between items-center pt-3 border-t-2 border-indigo-300">
                          <span className="text-base font-bold text-gray-800">Final Price:</span>
                          <span className="text-2xl font-extrabold text-indigo-600">
                            ₹
                            {(
                              parseFloat(formData.price) -
                              (parseFloat(formData.price) * parseFloat(formData.discount)) / 100
                            ).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                        <div className="mt-2 text-xs text-gray-500 text-right">
                          You save{" "}
                          <span className="font-bold text-green-600">
                            ₹
                            {(
                              (parseFloat(formData.price) * parseFloat(formData.discount)) /
                              100
                            ).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Discount Amount:</span>
                          <span className="text-lg font-bold text-red-600">
                            - ₹{parseFloat(formData.discount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                        <div className="flex justify-between items-center pt-3 border-t-2 border-indigo-300">
                          <span className="text-base font-bold text-gray-800">Final Price:</span>
                          <span className="text-2xl font-extrabold text-indigo-600">
                            ₹
                            {(parseFloat(formData.price) - parseFloat(formData.discount)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                        <div className="mt-2 text-xs text-gray-500 text-right">
                          You save{" "}
                          <span className="font-bold text-green-600">
                            ₹{parseFloat(formData.discount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-6">
            {errors._general && (
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-600">{errors._general}</p>
              </div>
            )}
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Add Classes</h3>
              <button
                type="button"
                onClick={() => {
                  setShowAddLessonForm(true);
                  setEditingLessonIndex(null);
                  setNewLesson({ title: "", description: "", duration: 0, order: 0 });
                }}
                className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Add Class
              </button>
            </div>

            {showAddLessonForm && (
              <div className="bg-gray-50 p-6 rounded-lg border-2 border-indigo-200 mb-6">
                <h4 className="font-semibold mb-4">
                  {editingLessonIndex !== null ? "Edit Class" : "New Class"}
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Class Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newLesson.title}
                      onChange={(e) => setNewLesson({ ...newLesson, title: e.target.value })}
                      placeholder="e.g. Introduction to Variables"
                      className="w-full rounded-none border-2 border-black shadow-sm focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 sm:text-sm pl-4 py-2"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={editingLessonIndex !== null ? handleUpdateLesson : handleAddLesson}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                    >
                      {editingLessonIndex !== null ? "Update" : "Create"} Class
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddLessonForm(false);
                        setEditingLessonIndex(null);
                        setNewLesson({ title: "", description: "", duration: 0, order: 0 });
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {lessons.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No classes added yet. Click "Add Class" to get started.</p>
              ) : (
                lessons.map((lesson, index) => (
                  <div key={index} className="bg-white border-2 border-gray-200 rounded-lg p-4">
                    {/* Dropdown Header - use div to avoid button nested in button (Edit/Delete are buttons) */}
                    <div className="flex justify-between items-center">
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setExpandedClassIndex(expandedClassIndex === index ? null : index)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedClassIndex(expandedClassIndex === index ? null : index); } }}
                        className="flex-1 flex items-center justify-between text-left hover:bg-gray-50 p-2 rounded-md transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-2">
                          {expandedClassIndex === index ? (
                            <ChevronUpIcon className="h-5 w-5 text-gray-600" />
                          ) : (
                            <ChevronDownIcon className="h-5 w-5 text-gray-600" />
                          )}
                          <h4 className="font-semibold text-gray-800">{lesson.title || `Class ${index + 1}`}</h4>
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditLesson(index);
                            }}
                            className="p-1.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                            title="Edit"
                          >
                            <PencilSquareIcon className="h-5 w-5" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteLesson(index);
                            }}
                            className="px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-sm"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Dropdown Content */}
                    {expandedClassIndex === index && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              // First, ensure course is saved
                              let currentCourseId = courseId;
                              if (!currentCourseId) {
                                // Validate step 1 before saving
                                if (!validateStep(1)) {
                                  toast.error("Please complete the basic information first");
                                  return;
                                }
                                
                                // Auto-save the course
                                toast.info("Saving course...");
                                try {
                                  currentCourseId = await handleSaveDraft(true);
                                } catch (saveError) {
                                  console.error("Error saving course:", saveError);
                                  toast.error(saveError.response?.data?.message || "Failed to save course. Please check the console for details.");
                                  return;
                                }
                                
                                if (!currentCourseId) {
                                  toast.error("Failed to save course. Please try again.");
                                  return;
                                }
                              }

                              // Now ensure lesson has an id
                              let currentLesson = lesson;
                              if (!currentLesson.id && currentCourseId) {
                                // Save lesson to get id
                                try {
                                  console.log("Saving lesson with data:", {
                                    course_id: currentCourseId,
                                    title: currentLesson.title,
                                    description: currentLesson.description || "",
                                    order: currentLesson.order || index + 1,
                                    videoUrl: currentLesson.videoUrl || null,
                                  });
                                  
                                  const response = await axios.post(
                                    `${API_URL}/lessons`,
                                    {
                                      course_id: currentCourseId,
                                      title: currentLesson.title,
                                      description: currentLesson.description || "",
                                      order: currentLesson.order || index + 1,
                                      videoUrl: currentLesson.videoUrl || null,
                                    },
                                    {
                                      headers: {
                                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                                      },
                                    }
                                  );
                                  
                                  console.log("Lesson saved successfully:", response.data);
                                  const updatedLessons = [...lessons];
                                  updatedLessons[index] = { ...currentLesson, id: response.data.id };
                                  setLessons(updatedLessons);
                                  currentLesson = updatedLessons[index];
                                } catch (lessonError) {
                                  console.error("Error saving lesson:", lessonError);
                                  console.error("Lesson error response:", lessonError.response?.data);
                                  toast.error(
                                    lessonError.response?.data?.error || 
                                    lessonError.response?.data?.message || 
                                    "Failed to save lesson. Please check the console for details."
                                  );
                                  return;
                                }
                              }

                              // Open the content modal
                              if (currentLesson.id && currentCourseId) {
                                // Ensure lesson has required properties
                                const lessonForModal = {
                                  ...currentLesson,
                                  id: currentLesson.id,
                                  title: currentLesson.title || `Class ${index + 1}`,
                                  description: currentLesson.description || "",
                                  duration: currentLesson.duration || 0,
                                  order: currentLesson.order || index + 1,
                                  contents: currentLesson.contents || []
                                };
                                
                                console.log("Opening content modal with:", {
                                  courseId: currentCourseId,
                                  lesson: lessonForModal
                                });
                                
                                setContentModalCourseId(currentCourseId);
                                setSelectedLessonForContent(lessonForModal);
                                setShowContentModal(true);
                              } else {
                                console.error("Missing required data:", {
                                  lessonId: currentLesson.id,
                                  courseId: currentCourseId,
                                  lesson: currentLesson
                                });
                                toast.error("Failed to save lesson or course. Please try again.");
                              }
                            } catch (error) {
                              console.error("Error opening content modal:", error);
                              console.error("Error details:", {
                                message: error.message,
                                response: error.response?.data,
                                courseId: courseId,
                                lesson: lesson
                              });
                              toast.error(
                                error.response?.data?.message || 
                                error.message || 
                                "Failed to open content manager. Please try again."
                              );
                            }
                          }}
                          className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium"
                        >
                          Add Content
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        );
      case 4:
        // Step 4: Review – show all course data from steps 1, 2, 3
        const selectedInstructor = instructors.find((i) => String(i.id) === String(formData.instructorId));
        const instructorName = selectedInstructor
          ? `${selectedInstructor.firstName || ""} ${selectedInstructor.lastName || ""}`.trim() || selectedInstructor.email
          : "—";
        const finalPrice =
          formData.price && formData.discount
            ? formData.discountType === "percentage"
              ? parseFloat(formData.price) - (parseFloat(formData.price) * parseFloat(formData.discount)) / 100
              : parseFloat(formData.price) - parseFloat(formData.discount)
            : formData.price
            ? parseFloat(formData.price)
            : null;
        // Validation status for publishing (used in Validation section and for error display)
        const publishChecks = {
          title: !!(formData.title && formData.title.trim()),
          instructor: !!(formData.instructorId && formData.instructorId !== ""),
          duration: !formData.duration || (parseFloat(formData.duration) > 0 && !isNaN(parseFloat(formData.duration))),
          price: !formData.price || (parseFloat(formData.price) >= 0 && !isNaN(parseFloat(formData.price))),
          discount: (() => {
            if (!formData.discount) return true;
            if (formData.discountType === "percentage") {
              const n = parseFloat(formData.discount);
              return !isNaN(n) && n >= 0 && n <= 100;
            }
            if (formData.discountType === "total") {
              const n = parseFloat(formData.discount);
              if (isNaN(n) || n < 0) return false;
              if (formData.price && n > parseFloat(formData.price)) return false;
              return true;
            }
            return true;
          })(),
          atLeastOneClass: lessons.length > 0,
        };
        const allPublishValid = Object.values(publishChecks).every(Boolean);

        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Review Your Course</h3>
            <p className="text-gray-600 text-sm mb-6">
              Review all details from the previous steps before creating the course.
            </p>

            {/* Step 4: Validation error banner (user-friendly) */}
            {(errors._general || errors.title || errors.instructorId || errors.duration || errors.price || errors.discount) && (
              <div className="rounded-xl border-2 border-red-200 bg-red-50 p-4 shadow-sm">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-600" aria-hidden>
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                  </span>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-semibold text-red-800">Please fix the following before publishing</h4>
                    <ul className="mt-2 list-disc list-inside space-y-1 text-sm text-red-700">
                      {errors._general && <li>{errors._general}</li>}
                      {errors.title && <li>{errors.title}</li>}
                      {errors.instructorId && <li>{errors.instructorId}</li>}
                      {errors.duration && <li>{errors.duration}</li>}
                      {errors.price && <li>{errors.price}</li>}
                      {errors.discount && <li>{errors.discount}</li>}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Step 1: Basic Info - collapsible */}
            <div className="bg-white border-2 border-indigo-200 rounded-xl overflow-hidden shadow-sm">
              <button
                type="button"
                onClick={() => setStep4Expanded((prev) => ({ ...prev, basic: !prev.basic }))}
                className="w-full p-4 flex items-center justify-between gap-2 text-left hover:bg-indigo-50/50 transition-colors"
              >
                <h4 className="font-bold text-indigo-700 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-sm">1</span>
                  Basic Information
                </h4>
                <span className={`flex-shrink-0 w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 transition-transform ${step4Expanded.basic ? "rotate-180" : ""}`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </span>
              </button>
              {step4Expanded.basic && (
              <div className="px-6 pb-6 pt-0">
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="font-medium text-gray-500">Course Name</dt>
                  <dd className="mt-0.5 text-gray-900">{formData.title || "—"}</dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-500">Level</dt>
                  <dd className="mt-0.5 text-gray-900 capitalize">{formData.level || "—"}</dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-500">Instructor</dt>
                  <dd className="mt-0.5 text-gray-900">{instructorName}</dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-500">Categories</dt>
                  <dd className="mt-0.5 text-gray-900">{formData.categories || "—"}</dd>
                </div>
                <div className="md:col-span-2">
                  <dt className="font-medium text-gray-500">Description</dt>
                  <dd className="mt-0.5 text-gray-900 whitespace-pre-wrap">{formData.description || "—"}</dd>
                </div>
                {formData.learningObjectives && (
                  <div className="md:col-span-2">
                    <dt className="font-medium text-gray-500">Learning Objectives</dt>
                    <dd className="mt-0.5 text-gray-900 whitespace-pre-wrap">{formData.learningObjectives}</dd>
                  </div>
                )}
                {formData.prerequisites && (
                  <div className="md:col-span-2">
                    <dt className="font-medium text-gray-500">Prerequisites</dt>
                    <dd className="mt-0.5 text-gray-900 whitespace-pre-wrap">{formData.prerequisites}</dd>
                  </div>
                )}
                {formData.targetAudience && (
                  <div className="md:col-span-2">
                    <dt className="font-medium text-gray-500">Target Audience</dt>
                    <dd className="mt-0.5 text-gray-900 whitespace-pre-wrap">{formData.targetAudience}</dd>
                  </div>
                )}
                {tagsArr.length > 0 && (
                  <div className="md:col-span-2">
                    <dt className="font-medium text-gray-500">Tags</dt>
                    <dd className="mt-0.5 flex flex-wrap gap-2">
                      {tagsArr.map((tag) => (
                        <span key={tag} className="inline-flex px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs">
                          {tag}
                        </span>
                      ))}
                    </dd>
                  </div>
                )}
                {preview && (
                  <div className="md:col-span-2">
                    <dt className="font-medium text-gray-500 mb-2">Thumbnail</dt>
                    <dd>
                      <img src={preview} alt="Course thumbnail" className="h-32 w-48 rounded-lg border border-gray-200 object-cover" />
                    </dd>
                  </div>
                )}
              </dl>
              </div>
              )}
            </div>

            {/* Step 2: Pricing & Duration - collapsible */}
            <div className="bg-white border-2 border-indigo-200 rounded-xl overflow-hidden shadow-sm">
              <button
                type="button"
                onClick={() => setStep4Expanded((prev) => ({ ...prev, pricing: !prev.pricing }))}
                className="w-full p-4 flex items-center justify-between gap-2 text-left hover:bg-indigo-50/50 transition-colors"
              >
                <h4 className="font-bold text-indigo-700 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-sm">2</span>
                  Pricing & Duration
                </h4>
                <span className={`flex-shrink-0 w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 transition-transform ${step4Expanded.pricing ? "rotate-180" : ""}`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </span>
              </button>
              {step4Expanded.pricing && (
              <div className="px-6 pb-6 pt-0">
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="font-medium text-gray-500">Duration</dt>
                  <dd className="mt-0.5 text-gray-900">{formData.duration ? `${formData.duration} minutes` : "—"}</dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-500">Original Price</dt>
                  <dd className="mt-0.5 text-gray-900">
                    {formData.price != null && formData.price !== ""
                      ? `₹${parseFloat(formData.price).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : "—"}
                  </dd>
                </div>
                {(formData.discount && parseFloat(formData.discount) > 0) && (
                  <>
                    <div>
                      <dt className="font-medium text-gray-500">Discount</dt>
                      <dd className="mt-0.5 text-gray-900">
                        {formData.discountType === "percentage"
                          ? `${formData.discount}%`
                          : `₹${parseFloat(formData.discount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-medium text-gray-500">Final Price</dt>
                      <dd className="mt-0.5 text-lg font-semibold text-indigo-600">
                        {finalPrice != null ? `₹${finalPrice.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}
                      </dd>
                    </div>
                  </>
                )}
              </dl>
              </div>
              )}
            </div>

            {/* Step 3: Classes / Lessons - collapsible */}
            <div className="bg-white border-2 border-indigo-200 rounded-xl overflow-hidden shadow-sm">
              <button
                type="button"
                onClick={() => setStep4Expanded((prev) => ({ ...prev, classes: !prev.classes }))}
                className="w-full p-4 flex items-center justify-between gap-2 text-left hover:bg-indigo-50/50 transition-colors"
              >
                <h4 className="font-bold text-indigo-700 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-sm">3</span>
                  Classes Added
                </h4>
                <span className={`flex-shrink-0 w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 transition-transform ${step4Expanded.classes ? "rotate-180" : ""}`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </span>
              </button>
              {step4Expanded.classes && (
              <div className="px-6 pb-6 pt-0">
              {lessons.length === 0 ? (
                <p className="text-gray-500 text-sm">No classes added yet.</p>
              ) : (
                <ul className="space-y-3">
                  {lessons.map((lesson, index) => (
                    <li key={lesson.id || index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">
                        {index + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900">{lesson.title || `Class ${index + 1}`}</div>
                        {lesson.description && (
                          <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{lesson.description}</div>
                        )}
                        {lesson.duration != null && lesson.duration > 0 && (
                          <div className="text-xs text-gray-500 mt-1">Duration: {lesson.duration} min</div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              </div>
              )}
            </div>

            {/* Validation For Publishing Course - collapsible, 2-column grid in a box */}
            <div className="rounded-2xl border-2 border-indigo-100 bg-gradient-to-b from-indigo-50/80 to-white overflow-hidden shadow-sm">
              <button
                type="button"
                onClick={() => setStep4Expanded((prev) => ({ ...prev, validation: !prev.validation }))}
                className="w-full p-4 flex items-center justify-between gap-2 text-left hover:bg-indigo-100/50 transition-colors"
              >
                <h4 className="font-bold text-indigo-800 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-sm" aria-hidden>
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                  </span>
                  Validation For Publishing Course
                </h4>
                <span className={`flex-shrink-0 w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 transition-transform ${step4Expanded.validation ? "rotate-180" : ""}`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </span>
              </button>
              {step4Expanded.validation && (
              <div className="px-6 pb-6 pt-0">
              <p className="text-sm text-gray-600 mb-4">
                All items below must pass before you can publish. Go back to earlier steps to fix any failed checks.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className={`flex items-center gap-3 rounded-xl border-2 p-4 ${publishChecks.title ? "border-emerald-200 bg-emerald-50/60" : "border-red-200 bg-red-50/60"}`}>
                  <span className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${publishChecks.title ? "bg-emerald-500 text-white" : "bg-red-500 text-white"}`}>
                    {publishChecks.title ? <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg> : <span className="text-lg leading-none">×</span>}
                  </span>
                  <div className="min-w-0">
                    <span className="font-medium text-gray-900">Course title</span>
                    <p className="text-sm text-gray-600">{publishChecks.title ? "Title is set" : "Title is required"}</p>
                  </div>
                </div>
                <div className={`flex items-center gap-3 rounded-xl border-2 p-4 ${publishChecks.instructor ? "border-emerald-200 bg-emerald-50/60" : "border-red-200 bg-red-50/60"}`}>
                  <span className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${publishChecks.instructor ? "bg-emerald-500 text-white" : "bg-red-500 text-white"}`}>
                    {publishChecks.instructor ? <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg> : <span className="text-lg leading-none">×</span>}
                  </span>
                  <div className="min-w-0">
                    <span className="font-medium text-gray-900">Instructor</span>
                    <p className="text-sm text-gray-600">{publishChecks.instructor ? "Instructor is set" : "Instructor is required"}</p>
                  </div>
                </div>
                <div className={`flex items-center gap-3 rounded-xl border-2 p-4 ${publishChecks.duration ? "border-emerald-200 bg-emerald-50/60" : "border-red-200 bg-red-50/60"}`}>
                  <span className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${publishChecks.duration ? "bg-emerald-500 text-white" : "bg-red-500 text-white"}`}>
                    {publishChecks.duration ? <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg> : <span className="text-lg leading-none">×</span>}
                  </span>
                  <div className="min-w-0">
                    <span className="font-medium text-gray-900">Duration</span>
                    <p className="text-sm text-gray-600">{publishChecks.duration ? "Valid or not set" : "Must be a positive number if set"}</p>
                  </div>
                </div>
                <div className={`flex items-center gap-3 rounded-xl border-2 p-4 ${publishChecks.price ? "border-emerald-200 bg-emerald-50/60" : "border-red-200 bg-red-50/60"}`}>
                  <span className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${publishChecks.price ? "bg-emerald-500 text-white" : "bg-red-500 text-white"}`}>
                    {publishChecks.price ? <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg> : <span className="text-lg leading-none">×</span>}
                  </span>
                  <div className="min-w-0">
                    <span className="font-medium text-gray-900">Price</span>
                    <p className="text-sm text-gray-600">{publishChecks.price ? "Valid or not set" : "Must be ≥ 0 if set"}</p>
                  </div>
                </div>
                <div className={`flex items-center gap-3 rounded-xl border-2 p-4 ${publishChecks.discount ? "border-emerald-200 bg-emerald-50/60" : "border-red-200 bg-red-50/60"}`}>
                  <span className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${publishChecks.discount ? "bg-emerald-500 text-white" : "bg-red-500 text-white"}`}>
                    {publishChecks.discount ? <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg> : <span className="text-lg leading-none">×</span>}
                  </span>
                  <div className="min-w-0">
                    <span className="font-medium text-gray-900">Discount</span>
                    <p className="text-sm text-gray-600">{publishChecks.discount ? "Valid or not set" : "Check percentage (0–100) or amount ≤ price"}</p>
                  </div>
                </div>
                <div className={`flex items-center gap-3 rounded-xl border-2 p-4 ${publishChecks.atLeastOneClass ? "border-emerald-200 bg-emerald-50/60" : "border-red-200 bg-red-50/60"}`}>
                  <span className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${publishChecks.atLeastOneClass ? "bg-emerald-500 text-white" : "bg-red-500 text-white"}`}>
                    {publishChecks.atLeastOneClass ? <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg> : <span className="text-lg leading-none">×</span>}
                  </span>
                  <div className="min-w-0">
                    <span className="font-medium text-gray-900">At least one class</span>
                    <p className="text-sm text-gray-600">{publishChecks.atLeastOneClass ? `${lessons.length} class(es) added` : "Please add at least one class before publishing."}</p>
                  </div>
                </div>
              </div>
              {allPublishValid && (
                <p className="mt-4 text-sm font-medium text-emerald-700 flex items-center gap-2">
                  <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                  All validations passed. You can publish this course.
                </p>
              )}
              </div>
              )}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const totalSteps = 4;
  const stepLabels = [
    "Basic Info",
    "Pricing",
    "Add Class",
    "Review",
  ];

  const handleStepClick = (step) => {
    if (step <= currentStep || validateStep(currentStep)) {
      setCurrentStep(step);
    }
  };

  if (loadingCourse) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full -m-6">
      <div className="bg-white shadow-lg max-w-7xl mx-auto w-full flex flex-col h-full">
        {/* Edit mode title */}
        {isEditMode && (
          <div className="flex-shrink-0 px-6 pt-4 pb-1 border-b border-gray-100">
            <h2 className="text-lg font-bold text-gray-800">Edit Course</h2>
            <p className="text-sm text-gray-500 mt-0.5">Update course details and content using the steps below.</p>
          </div>
        )}
        {/* Fixed Header with Steps - Attached to top */}
        <div className="flex-shrink-0 p-3 border-b border-gray-200 bg-white">
          {/* Progress Steps */}
          <div className="flex items-center justify-between">
            {[1, 2, 3, 4].map((step) => (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center flex-1">
                  <button
                    type="button"
                    onClick={() => handleStepClick(step)}
                    disabled={step > currentStep && !checkStepValid(currentStep)}
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
                      currentStep >= step
                        ? "bg-indigo-600 text-white shadow-lg hover:bg-indigo-700 cursor-pointer"
                        : step > currentStep
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-gray-200 text-gray-600 hover:bg-gray-300 cursor-pointer"
                    }`}
                  >
                    {step}
                  </button>
                  <span className="mt-1 text-xs font-medium text-gray-700 text-center">
                    {stepLabels[step - 1]}
                  </span>
                </div>
                {step < 4 && (
                  <div
                    className={`flex-1 h-1.5 mx-2 rounded-full ${
                      currentStep > step ? "bg-indigo-600" : "bg-gray-200"
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
          {/* Mobile step indicator */}
          <div className="sm:hidden text-center mt-2">
            <span className="text-xs font-medium text-gray-700">
              Step {currentStep} of {totalSteps}: {stepLabels[currentStep - 1]}
            </span>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto p-6">
          <div>{renderStepContent()}</div>
        </div>

        {/* Fixed Navigation Buttons */}
        <div className="flex-shrink-0 flex justify-between items-center p-6 border-t border-gray-200 bg-white">
          <div className="flex gap-3">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={handlePrevious}
                className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Previous
              </button>
            )}
            <button
              type="button"
              disabled={savingDraft}
              onClick={() => handleSaveDraft()}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {savingDraft ? "Saving…" : "Save as Draft"}
            </button>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate("/courses")}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            {currentStep < 4 ? (
              <button
                type="button"
                onClick={handleNext}
                className="px-6 py-2 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                className="px-6 py-2 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {isEditMode ? "Update & Publish Course" : "Create & Publish Course"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Category management modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-800">Manage Categories</h3>
              <button
                type="button"
                onClick={() => {
                  setShowCategoryModal(false);
                  setCategoryModalInput("");
                  setEditingCategoryValue(null);
                  setEditingCategoryInput("");
                }}
                className="text-gray-500 hover:text-gray-700 p-1 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                ×
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={categoryModalInput}
                  onChange={(e) => setCategoryModalInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddCategoryFromModal()}
                  placeholder="New category name"
                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <button
                  type="button"
                  onClick={handleAddCategoryFromModal}
                  className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  Add
                </button>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">All categories</p>
                <ul className="border border-gray-200 rounded-md divide-y divide-gray-200 max-h-64 overflow-y-auto">
                  {categoryOptions.length === 0 ? (
                    <li className="px-3 py-4 text-sm text-gray-500 text-center">No categories yet. Add one above.</li>
                  ) : (
                    categoryOptions.map((cat) => (
                      <li key={cat.value} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50">
                        {editingCategoryValue === cat.value ? (
                          <>
                            <input
                              type="text"
                              value={editingCategoryInput}
                              onChange={(e) => setEditingCategoryInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") handleSaveEditCategory();
                                if (e.key === "Escape") {
                                  setEditingCategoryValue(null);
                                  setEditingCategoryInput("");
                                }
                              }}
                              className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                              autoFocus
                            />
                            <button
                              type="button"
                              onClick={handleSaveEditCategory}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                              title="Save"
                            >
                              <CheckIcon className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="flex-1 text-sm text-gray-900">{cat.label}</span>
                            <button
                              type="button"
                              onClick={() => handleStartEditCategory(cat)}
                              className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                              title="Edit"
                            >
                              <PencilSquareIcon className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteCategoryFromModal(cat)}
                              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                              title="Delete"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </li>
                    ))
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Course Content Manager Modal */}
      {showContentModal && selectedLessonForContent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-end items-center p-2 pr-3 border-b border-gray-100">
              <button
                type="button"
                onClick={() => {
                  setShowContentModal(false);
                  setSelectedLessonForContent(null);
                  setContentModalCourseId(null);
                }}
                className="text-gray-500 hover:text-gray-700 p-1 rounded text-2xl font-bold leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-hidden flex flex-col p-4">
              {contentModalCourseId && selectedLessonForContent?.id ? (
                <CourseContentManagerModal
                  courseId={contentModalCourseId}
                  lesson={selectedLessonForContent}
                  courseName={formData.title}
                  onClose={() => {
                    setShowContentModal(false);
                    setSelectedLessonForContent(null);
                    setContentModalCourseId(null);
                  }}
                  onContentUpdate={() => {
                    // Refresh lesson data after content update
                    if (selectedLessonForContent?.id) {
                      axios
                        .get(`${API_URL}/lessons/${selectedLessonForContent.id}`, {
                          headers: {
                            Authorization: `Bearer ${localStorage.getItem("token")}`,
                          },
                        })
                        .then((response) => {
                          const updatedLessons = [...lessons];
                          const lessonIndex = updatedLessons.findIndex(
                            (l) => l.id === selectedLessonForContent.id
                          );
                          if (lessonIndex !== -1) {
                            updatedLessons[lessonIndex] = response.data;
                            setLessons(updatedLessons);
                            setSelectedLessonForContent(response.data);
                          }
                        })
                        .catch((error) => {
                          console.error("Error refreshing lesson:", error);
                        });
                    }
                  }}
                />
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600 mb-4">
                    {!contentModalCourseId 
                      ? "Please save the course first before adding content."
                      : "Lesson information is missing. Please try again."}
                  </p>
                  {!contentModalCourseId && (
                    <button
                      type="button"
                      onClick={async () => {
                        const savedCourseId = await handleSaveDraft();
                        if (savedCourseId) {
                          setContentModalCourseId(savedCourseId);
                        }
                      }}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                    >
                      Save Course First
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewCourse;
